import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { THREAD_API_URL } from '../config';
import type {
  GetThreadsLastActivityRequest,
  GetThreadsLastActivityResponse,
  UnreadThread,
} from './apiTypes/endpoints/threads';
import { ApiError, PagedResponse } from './apiTypes/misc';
import { threadsLastActivityPath } from './apiTypes/paths/threads';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import * as api from './requests';
import { getAuthToken } from './requests';

export enum ChannelRole {
  CHANNEL_MEMBER = 'channel_member',
  CHANNEL_OWNER = 'channel_owner',
  CHANNEL_SPECTATOR = 'channel_spectator',
}

export enum RootEntityType {
  MESSAGE = 'message',
}

export type RootEntityId = string;

export type ThreadFilters = {
  unread?: boolean;
  query?: string;
};

export type RootEntityContextParams = {
  school_id: string;
  root_entity_type: RootEntityType;
  root_entity_id: RootEntityId;
};

export enum ThreadStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export type ThreadUser = {
  profile_picture: string | null;
  last_name: string;
  given_name: string;
  known_as: string | null;
  user_id: string;
  relation_id: string;
};

export type ThreadChild = {
  primary_contact_id: string;
} & ThreadUser;

export type ChannelMember = {
  user: ThreadUser;
  role: ChannelRole;
};

export type Channel = {
  id: string;
  members: ChannelMember[];
} & ChannelUnread;

export type ChannelUnread = {
  message_count: number;
  last_read_message_id: string | null;
  last_user_message: ChannelMessage;
  last_channel_message: ChannelMessage;
  unread_count: number;
};

export type ChannelMessage = {
  created_at?: string;
  updated_at?: string | null;
  sender: ThreadUser | null;
  text: string;
  status?: string;
};

export type ThreadUnreadData = Record<RootEntityId, Array<UnreadThread>>;

export type Thread = {
  id: string;
  name: string | null;
  channel: Channel | null;
  status: ThreadStatus;
  parents: ThreadUser[];
  children: ThreadChild[];
  invited_staff: ThreadUser[];
  spectators: ThreadUser[];
  creator_id: string;
} & RootEntityContextParams;

export type GetConnectionResponse = {
  connection_id: string;
  token: string;
  created_at: number;
  expires_at: number;
};

export type GetThreadsParams = {
  page_size?: number;
  page_number?: number;
} & RootEntityContextParams &
  ThreadFilters;

export enum ThreadAggregateOptions {
  ROOT_ENTITY_STATUS = 'root_entity_status',
}

export type ThreadAggregateData = {
  root_entity_status: ThreadStatus;
};

export type ThreadsAggregateResponse = { data: Record<RootEntityId, ThreadAggregateData> };

export type GetThreadsAggregateParams = {
  include: [ThreadAggregateOptions];
} & RootEntityContextParams;

export type GetThreadParams = {
  thread_id: string;
  school_id: string;
};

export type UpdateThreadMembersParams = {
  thread_id: string;
  school_id: string;
  new_members?: ThreadUser[];
  spectators?: ThreadUser[];
};

export type UpdateThreadArchiveParams = {
  thread_id: string;
  school_id: string;
  archive: boolean;
};

export type GetUnreadThreadsParams = {
  root_entity_id?: string;
} & Omit<RootEntityContextParams, 'root_entity_id'>;

export type GetUnreadThreadsResponse = {
  unread_threads: Record<RootEntityType, ThreadUnreadData>;
};

export type UpdateThreadParams = RootEntityContextParams & {
  thread_ids?: string[];
  status?: ThreadStatus;
};

export const getThreadsAggregate = async (
  params: GetThreadsAggregateParams,
): Promise<ThreadsAggregateResponse> => {
  return api.post(`${THREAD_API_URL}/threads/aggregate`, params);
};

export const getThreads = async (params: GetThreadsParams): Promise<PagedResponse<Thread>> => {
  return api.get(`${THREAD_API_URL}/threads`, {
    params,
  });
};

export const getThread = async (params: GetThreadParams): Promise<Thread> => {
  const { thread_id, ...queryParams } = params;
  return api.get(`${THREAD_API_URL}/threads/${thread_id}`, {
    params: queryParams,
  });
};

export const createConnection = async (params: {
  school_id: string;
}): Promise<GetConnectionResponse> => {
  return api.post(`${THREAD_API_URL}/threads/connection`, params);
};

export const updateConnection = async (params: {
  connection_id: string;
  school_id: string;
}): Promise<GetConnectionResponse> => {
  return api.patch(`${THREAD_API_URL}/threads/connection/${params.connection_id}`, {
    school_id: params.school_id,
  });
};

export const deleteConnection = async (params: {
  connection_id: string;
  school_id: string;
}): Promise<void> => {
  return api.remove(`${THREAD_API_URL}/threads/connection/${params.connection_id}`, {
    school_id: params.school_id,
  });
};

export const deleteConnectionOnUnload = async (params: {
  connection_id: string;
  school_id: string;
}): Promise<void> => {
  const url = `${THREAD_API_URL}/threads/connection/${params.connection_id}`;

  const Authorization = await getAuthToken();

  // Use fetch with keepalive for page unload scenarios
  await fetch(url, {
    method: 'DELETE',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization,
    },
    body: JSON.stringify({ school_id: params.school_id }),
  });
};

export const createThreadChannel = async ({
  thread_id,
  ...params
}: GetThreadParams): Promise<Thread> => {
  return api.post(`${THREAD_API_URL}/threads/${thread_id}`, params);
};

export const updateThreadMembers = async (params: UpdateThreadMembersParams): Promise<Thread> => {
  const { thread_id, ...bodyParams } = params;
  return api.patch(`${THREAD_API_URL}/threads/${thread_id}/members`, bodyParams);
};

export const updateThread = async (params: UpdateThreadParams): Promise<Thread> => {
  return api.patch(`${THREAD_API_URL}/threads`, params);
};

export const getUnreadThreads = async (
  params: GetUnreadThreadsParams,
): Promise<GetUnreadThreadsResponse> => {
  return api.get(`${THREAD_API_URL}/threads/unread`, {
    params,
  });
};

export const GET_THREADS_QUERY = `${THREAD_API_URL}/GET_THREADS_QUERY`;
export const GET_THREAD_QUERY = `${THREAD_API_URL}/GET_THREAD_QUERY`;
export const GET_CONNECTION_QUERY = `${THREAD_API_URL}/GET_CONNECTION_QUERY`;
export const GET_CONNECT_USER_QUERY = `${THREAD_API_URL}/GET_CONNECT_USER_QUERY`;
export const GET_UNREAD_THREADS_QUERY = `${THREAD_API_URL}/GET_UNREAD_THREADS_QUERY`;
export const GET_THREADS_AGGREGATE_QUERY = `${THREAD_API_URL}/GET_THREADS_AGGREGATE_QUERY`;
export const UPDATE_THREAD_MUTATION = 'UPDATE_THREAD_MUTATION';

export const useGetUnreadThreadsQuery = (
  params: GetUnreadThreadsParams,
  options?: RQUseQueryOptions<GetUnreadThreadsResponse>,
) => {
  return useQuery<GetUnreadThreadsResponse, ApiError>(
    [GET_UNREAD_THREADS_QUERY, params],
    () => getUnreadThreads(params),
    options,
  );
};

export const useGetThreadsQuery = (
  initialParams: GetThreadsParams,
  options?: RQUseInfiniteQueryOptions<PagedResponse<Thread>>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<PagedResponse<Thread>, ApiError>(
    [GET_THREADS_QUERY, { ...params }],
    ({ pageParam }) =>
      getThreads({
        page_number: pageParam,
        ...params,
      }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export const useGetThreadQuery = (params: GetThreadParams, options?: RQUseQueryOptions<Thread>) => {
  return useQuery<Thread, ApiError>([GET_THREAD_QUERY, params], () => getThread(params), options);
};

export const useCreateThreadChannel = (
  options?: RQUseMutationOptions<Thread, GetThreadParams>,
): RQUseMutationResult<Thread, GetThreadParams> => {
  return useMutation(createThreadChannel, options);
};

export const useUpdateThreadMembers = (
  options?: RQUseMutationOptions<Thread, UpdateThreadMembersParams>,
): RQUseMutationResult<Thread, UpdateThreadMembersParams> => {
  return useMutation(updateThreadMembers, options);
};

export const useUpdateThread = (
  options?: RQUseMutationOptions<Thread, UpdateThreadParams>,
): RQUseMutationResult<Thread, UpdateThreadParams> => {
  return useMutation([UPDATE_THREAD_MUTATION], updateThread, options);
};

export const useGetThreadsAggregateQuery = (
  params: GetThreadsAggregateParams,
  options?: RQUseQueryOptions<ThreadsAggregateResponse>,
) => {
  return useQuery<ThreadsAggregateResponse, ApiError>(
    [GET_THREADS_AGGREGATE_QUERY, params],
    () => getThreadsAggregate(params),
    options,
  );
};

export const getThreadsLastActivity = async (
  params: GetThreadsLastActivityRequest,
): Promise<GetThreadsLastActivityResponse> => {
  return api.post(`${THREAD_API_URL}${threadsLastActivityPath()}`, params);
};

export const GET_THREADS_LAST_ACTIVITY_QUERY = `${THREAD_API_URL}/GET_THREADS_LAST_ACTIVITY_QUERY`;

export const useGetThreadsLastActivityQuery = (
  params: GetThreadsLastActivityRequest,
  options?: RQUseQueryOptions<GetThreadsLastActivityResponse>,
) => {
  return useQuery<GetThreadsLastActivityResponse, ApiError>(
    [GET_THREADS_LAST_ACTIVITY_QUERY, params],
    () => getThreadsLastActivity(params),
    options,
  );
};
